import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback, useState, type ReactNode, type Ref } from 'react'

import { ProductButtonBagDS, useProductButtonBagDSVariant } from '@src/components/blocks/product-button-bag-ds'
import { mergeLegacyResponsiveStyles } from '@src/components/blocks/rich-text-ds/classname-to-ds-style'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ExpressPay } from '@src/modules/express-pay'
import { useIsExpressPayEnabled } from '@src/modules/express-pay/express-pay'
import { BUTTON_TYPES } from '@src/quince-components/src/atoms/button'
import { ProductCTAConstants } from '@src/quince-components/src/atoms/productCTA/constants'
import { getTrackingObject } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import type { ProductCTAType } from '@src/quince-components/src/atoms/productCTA/types'
import { useGoldBarProductEligibility } from '@src/quince-components/src/atoms/productCTA/useGoldBarProductEligibility'
import { getIsGiftCard } from '@src/quince-components/src/atoms/productCTA/utils'
import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import WaitlistSmsModal from '@src/quince-components/src/atoms/waitlist-sms-modal'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { useCart } from '@src/quince-site-common/src/hooks/cart'
import useATCVsATBExperiment from '@src/quince-site-common/src/hooks/use-atc-vs-atb-experiment'
import { useWaitlistSmsModal } from '@src/quince-site-common/src/hooks/use-waitlist-sms-modal'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import { SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/constant'

import {
  buttonWrapperCss,
  defaultCTAHiddenCss,
  expressPayRowCss,
  expressPayRowReadyCss,
  hideKlarnaCss,
  pointerEventsNoneClassCss,
  pointerEventsNoneCss,
  postOrderCtaWrapperCss,
  quickATCWrapperCss,
} from './product-cta-ds.styles'
import { ProductDisplayButtonDS } from './product-display-button-ds'
import { RenderErrorMessageDS } from './product-message-ds'
import { ProductWaitlistCTADS } from './product-waitlist-cta-ds'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

/**
 * ProductCTADS — pure Design-System twin of `ProductCTAWrapper` (default export of legacy
 * productCTA.tsx). Rendered only on the DS path; prop-compatible with `ProductCTAType` so it is a
 * drop-in for `ProductCTAWrapper`.
 *
 * Logic is reused verbatim from the shared hooks/utils — `useProductCTAType` resolves the CTA type
 * upstream and is passed in via `props.type` exactly as the legacy wrapper receives it, while
 * `useGoldBarProductEligibility`, `useATCVsATBExperiment`, `useWaitlistSmsModal`,
 * `useTrackIsElementVisible`, `getTrackingObject`, `CTA_TYPE` and `ProductCTAConstants` are imported,
 * never reimplemented.
 *
 * Message note: the in-stock / non-physical / EDD / holiday messages are owned by the
 * `ProductButtonBagDS` block (variant-driven) — this component renders only the CTA region:
 * error messages, buttons, waitlist input and ExpressPay.
 */
const ProductCTADS = (props: ProductCTAType) => {
  const {
    selectedVariantPurchaseInfo,
    onJoinWaitList,
    product,
    expressPayConfig,
    isCurrentProductOptionLeavingSoon,
    disableAddToCart,
    isQuickATCModal,
    isFromCartPopup,
    handleShopInApp,
    enableExpressPayAnimation,
    hideKlarna,
    purchaseOption,
    selectedPlanId,
    showPurchaseTypeSelector,
  } = props

  // `ProductCTAType` types `type`/`addToCartRef` as `any`; narrow locally so downstream stays type-safe.
  const type = props.type as CTA_TYPE
  const addToCartRef = props.addToCartRef as Ref<HTMLButtonElement> | undefined

  const { t } = useTranslation('common')
  const { isB2BMode } = useStorefrontModeContext()
  const showATB = useATCVsATBExperiment()
  const atcLabel = showATB ? ProductCTAConstants.ADD_TO_BAG : ProductCTAConstants.ADD_TO_CART
  const selectedVariantId = selectedVariantPurchaseInfo?.selectedVariantId

  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { isBisWaitlistSmsEnabled, showSmsModal, setShowSmsModal, openSmsModalIfAllowed, onSubmitPhone } =
    useWaitlistSmsModal({ product, selectedOptions: props.selectedOptions })

  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)

  const { isBuyNowInProgress } = useCart()
  const isExpressPayEnabled = useIsExpressPayEnabled()
  const showExpressPay = expressPayConfig && !expressPayConfig.disabled && isExpressPayEnabled
  const [isApplePayReady, setIsApplePayReady] = useState(false)

  const handleApplePayReady = useCallback(() => {
    setIsApplePayReady(true)
  }, [])

  const EMAIL_ERROR_MSG = t('please_enter_valid_email_address')
  const ctaText = t('preorder')

  // Reset invalid-email message when the input is absent (parity with legacy revertEmailErrorMsg).
  const revertEmailErrorMsg = () => {
    if (errorMessage === EMAIL_ERROR_MSG) {
      setErrorMessage('')
    }
  }

  // Telemetry reused verbatim: preorder_button / waitlist_button IS_VISIBLE impressions.
  const { ref: trackIsVisiblePreorderRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('preorder_button', selectedVariantId), [selectedVariantId]),
  )
  const { ref: trackIsVisibleWaitlistRef } = useTrackIsElementVisible(
    useCallback(() => getTrackingObject('waitlist_button', selectedVariantId), [selectedVariantId]),
  )

  const handleAddToCart = (isPreorder = false, planId?: number) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    props.onAddToCart(isPreorder, planId)
  }

  const handleClick = () => {
    switch (type) {
      case CTA_TYPE.PREORDER:
        handleAddToCart(true)
        break

      case CTA_TYPE.WAITLIST:
        if (isCurrentProductOptionLeavingSoon) {
          // Fires the parent's `clicked_shop_similar` BUTTON_CLICK telemetry (context label SHOP_SIMILAR).
          props.onClickedShopSimilar?.()
          const element = document.querySelector(`[data-section-name="${SIMILAR_PRODUCTS_SECTION_NAME_FOR_REF}"]`)
          if (element) {
            // @i18n-ignore
            element.scrollIntoView({ behavior: 'smooth' })
          }
          return
        }
        if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
          return
        }
        if (AuthUtil.isLoggedIn()) {
          const cookieEmail =
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ??
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ??
            ''
          onJoinWaitList?.(cookieEmail)
          openSmsModalIfAllowed(cookieEmail)
        } else if (validateEmail(email)) {
          onJoinWaitList?.(email)
          setErrorMessage('')
          setEmail('')
          openSmsModalIfAllowed(email)
        } else {
          setErrorMessage(EMAIL_ERROR_MSG)
        }
        break

      case CTA_TYPE.ADD_TO_CART:
        if (props.isPostOrderFlow && props.handlePostOrderCallback) {
          props.handlePostOrderCallback()
        } else {
          handleAddToCart(
            false,
            showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && selectedPlanId
              ? selectedPlanId
              : undefined,
          )
        }
        break

      case CTA_TYPE.SHOP_IN_THE_APP:
        handleShopInApp?.({ product: props.product, selectedOptions: props.selectedOptions ?? [], isButtonClick: true })
        break

      default:
        // @i18n-ignore
        console.warn('Invalid type')
    }
  }

  const isDefaultCTAType =
    type !== CTA_TYPE.PREORDER &&
    type !== CTA_TYPE.WAITLIST &&
    type !== CTA_TYPE.OUT_OF_STOCK &&
    type !== CTA_TYPE.SHOP_IN_THE_APP
  const shouldKeepDefaultDesktopExpressPayRowMounted =
    showExpressPay && !props.isQuickATCForMobile && !props.isPostOrderFlow
  const shouldRenderDefaultCTA = isDefaultCTAType || shouldKeepDefaultDesktopExpressPayRowMounted

  let nonDefaultCTAContent: ReactNode = null
  switch (type) {
    case CTA_TYPE.PREORDER: {
      if (props.isPostOrderFlow) {
        nonDefaultCTAContent = (
          <ProductDisplayButtonDS
            onClick={() => undefined}
            disabled
            buttonCss={pointerEventsNoneCss}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t('out_of_stock')}
          </ProductDisplayButtonDS>
        )
        break
      }
      revertEmailErrorMsg()
      nonDefaultCTAContent = (
        <div
          data-testid={TEST_LOCATORS.PRODUCT_CTA}
          ref={trackIsVisiblePreorderRef}
          className={cx(buttonWrapperCss, hideKlarna ? hideKlarnaCss : undefined)}
        >
          {errorMessage ? (
            <RenderErrorMessageDS errorMessage={errorMessage} isQuickATCForMobile={props.isQuickATCForMobile} />
          ) : null}
          {props.isQuickATCForMobile ? (
            !props.isKeyboardVisibleForQuickATCForMobile && (
              <div className={quickATCWrapperCss}>
                <ProductDisplayButtonDS
                  onClick={handleClick}
                  showLoader={props.isAddToCartInProgress}
                  selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                  product={props.product}
                  selectedOptions={props.selectedOptions}
                  setIsCartPopUpOpen={props.setIsCartPopUpOpen}
                  isBuyNowPdp={props.isBuyNowPdp}
                  isFromCartPopup={isFromCartPopup}
                >
                  {ctaText}
                </ProductDisplayButtonDS>
              </div>
            )
          ) : (
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {ctaText}
            </ProductDisplayButtonDS>
          )}
        </div>
      )
      break
    }

    case CTA_TYPE.WAITLIST: {
      nonDefaultCTAContent = (
        <ProductWaitlistCTADS
          handleClick={handleClick}
          errorMessage={errorMessage}
          isQuickATCForMobile={props.isQuickATCForMobile}
          isBuyNowInProgress={isBuyNowInProgress}
          isAddToCartInProgress={props.isAddToCartInProgress}
          addToCartRef={addToCartRef}
          isKeyboardVisibleForQuickATCForMobile={props.isKeyboardVisibleForQuickATCForMobile}
          trackIsVisibleWaitlistRef={trackIsVisibleWaitlistRef}
          email={email}
          setEmail={setEmail}
          setIsKeyboardVisibleForQuickATCForMobile={props.setIsKeyboardVisibleForQuickATCForMobile}
          isPostOrderFlow={props.isPostOrderFlow}
          isCurrentProductOptionLeavingSoon={isCurrentProductOptionLeavingSoon}
          selectedVariantPurchaseInfo={selectedVariantPurchaseInfo}
          isQuickATCModal={isQuickATCModal}
          isFromCartPopup={isFromCartPopup}
        />
      )
      break
    }

    case CTA_TYPE.OUT_OF_STOCK: {
      nonDefaultCTAContent = (
        <>
          <RenderErrorMessageDS errorMessage={t('out_of_stock')} />
          <ProductDisplayButtonDS
            onClick={() => undefined}
            disabled
            buttonCss={pointerEventsNoneCss}
            product={props.product}
            selectedOptions={props.selectedOptions}
            setIsCartPopUpOpen={props.setIsCartPopUpOpen}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          >
            {t(atcLabel)}
          </ProductDisplayButtonDS>
        </>
      )
      break
    }

    case CTA_TYPE.SHOP_IN_THE_APP: {
      const shopInAppButton = (
        <ProductDisplayButtonDS
          onClick={handleClick}
          buttonType={BUTTON_TYPES.SECONDARY}
          product={props.product}
          selectedOptions={props.selectedOptions}
          setIsCartPopUpOpen={props.setIsCartPopUpOpen}
          isBuyNowPdp={props.isBuyNowPdp}
          isFromCartPopup={isFromCartPopup}
        >
          {t('shop_in_the_app')}
        </ProductDisplayButtonDS>
      )
      nonDefaultCTAContent = (
        <>
          {selectedVariantPurchaseInfo?.showWaitList ? <RenderErrorMessageDS errorMessage={t('out_of_stock')} /> : null}
          {props.isQuickATCForMobile
            ? // Pin the CTA to the sticky mobile footer, mirroring the ADD_TO_CART/PREORDER paths.
              !props.isKeyboardVisibleForQuickATCForMobile && (
                <div className={quickATCWrapperCss}>{shopInAppButton}</div>
              )
            : shopInAppButton}
        </>
      )
      break
    }

    default:
      revertEmailErrorMsg()
      break
  }

  // Default (ADD_TO_CART / B2B) CTA. Preserves the ExpressPay mounted-button pattern: when not
  // visible the wrapper keeps `visibility: hidden` (NOT display:none) so the node stays mounted +
  // measurable for ExpressPay, and `aria-hidden` is set on this non-focusable wrapper only — the
  // button inside is removed from the tab order by `visibility: hidden`, so no focusable element is
  // ever hidden from AT.
  const renderDefaultCTA = (isVisible: boolean) => {
    const buyNowCss: SystemStyleObject | undefined = isBuyNowInProgress ? pointerEventsNoneCss : undefined
    return (
      <div
        data-testid={isVisible ? TEST_LOCATORS.PRODUCT_CTA : undefined}
        className={cx(isVisible ? undefined : defaultCTAHiddenCss, hideKlarna ? hideKlarnaCss : undefined)}
        aria-hidden={!isVisible}
      >
        {isVisible && errorMessage ? (
          <RenderErrorMessageDS
            errorMessage={errorMessage}
            isQuickATCForMobile={props.isQuickATCForMobile}
            hideIcon={isGoldBarError}
            isBuyNowPdp={props.isBuyNowPdp}
            isFromCartPopup={isFromCartPopup}
          />
        ) : null}

        {isVisible && props.isQuickATCForMobile ? (
          !props.isKeyboardVisibleForQuickATCForMobile && (
            <div className={quickATCWrapperCss}>
              <ProductDisplayButtonDS
                onClick={handleClick}
                showLoader={props.isAddToCartInProgress}
                selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
                buttonRef={addToCartRef}
                disabled={isGoldBarError || props.disableAddToCart}
                buttonCss={buyNowCss}
                product={props.product}
                selectedOptions={props.selectedOptions}
                setIsCartPopUpOpen={props.setIsCartPopUpOpen}
                isBuyNowPdp={props.isBuyNowPdp}
                isFromCartPopup={isFromCartPopup}
              >
                {t(atcLabel)}
              </ProductDisplayButtonDS>
            </div>
          )
        ) : isVisible && props.isPostOrderFlow ? (
          <div className={postOrderCtaWrapperCss}>
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError || disableAddToCart}
              buttonCss={buyNowCss}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {t('continue_with_this_item')}
            </ProductDisplayButtonDS>
          </div>
        ) : !props.isQuickATCForMobile &&
          !props.isPostOrderFlow &&
          (isVisible || shouldKeepDefaultDesktopExpressPayRowMounted) ? (
          <div
            className={
              showExpressPay
                ? css(mergeLegacyResponsiveStyles(expressPayRowCss, isApplePayReady ? expressPayRowReadyCss : {}))
                : undefined
            }
          >
            <ProductDisplayButtonDS
              onClick={handleClick}
              showLoader={props.isAddToCartInProgress}
              selectorKey={QUINCE_BOT_LOCATORS.PDP.ADD_TO_CART}
              buttonRef={addToCartRef}
              disabled={isGoldBarError || disableAddToCart}
              buttonCss={buyNowCss}
              buttonType={isB2BMode ? BUTTON_TYPES.SECONDARY : undefined}
              product={props.product}
              selectedOptions={props.selectedOptions}
              setIsCartPopUpOpen={props.setIsCartPopUpOpen}
              isBuyNowPdp={props.isBuyNowPdp}
              isFromCartPopup={isFromCartPopup}
            >
              {t(atcLabel)}
            </ProductDisplayButtonDS>

            {showExpressPay ? (
              <ExpressPay
                config={expressPayConfig}
                className={isBuyNowInProgress ? pointerEventsNoneClassCss : undefined}
                dataSelectorKey={QUINCE_BOT_LOCATORS.PDP.APPLE_PAY_CLICK}
                productPrice={
                  showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && props.subscriptionPrice
                    ? props.subscriptionPrice
                    : props.salePrice
                }
                onReady={handleApplePayReady}
                enableAnimation={enableExpressPayAnimation}
                disableClick={isGoldBarError || disableAddToCart}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <>
      {nonDefaultCTAContent}
      {shouldRenderDefaultCTA ? renderDefaultCTA(isDefaultCTAType) : null}
      {isBisWaitlistSmsEnabled ? (
        <WaitlistSmsModal
          open={showSmsModal}
          onClose={() => {
            setShowSmsModal(false)
          }}
          onSubmitPhone={onSubmitPhone}
        />
      ) : null}
    </>
  )
}

/**
 * Wrapper props — `ProductCTAType` (names preserved) plus the Klarna vocabulary the
 * `ProductButtonBagDS` block needs (previously wired by hand at the product-form call site).
 */
export type ProductCTADSWrapperProps = ProductCTAType & {
  /** Festival offer price — the CTA/ExpressPay get `festivalOfferPrice ?? salePrice`; Klarna uses it for the amount. */
  festivalOfferPrice?: number
  /** `KlarnaWrapper.hidePayLater` — hides the pay-later placement only. */
  hidePayLater?: boolean
  /** Form-level guard (`use-product-form`'s `hideKlarnaFull`) — suppresses the Klarna region entirely. */
  hideKlarnaFull?: boolean
  /** `KlarnaWrapper.wrapperClassname` passthrough. */
  klarnaWrapperClassname?: string
}

/**
 * ProductCTADS default export — twin of legacy `ProductCTAWrapper`. Computes the Figma
 * ProductButtonBag `variant` from purchase state (`useProductButtonBagDSVariant`) and renders the
 * `ProductButtonBagDS` block with it: the block owns the message region (holiday / EDD / in-stock /
 * email) and the Klarna message, while the CTA region (`ProductCTADS`) is passed as children so
 * click handling, ExpressPay and telemetry stay here.
 */
const ProductCTADSWrapper = (props: ProductCTADSWrapperProps) => {
  const { festivalOfferPrice, hidePayLater, hideKlarnaFull, klarnaWrapperClassname, ...ctaProps } = props
  const variant = useProductButtonBagDSVariant(ctaProps)
  const { isGoldBarError } = useGoldBarProductEligibility(props.product)
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  // EDD-visibility gate copied verbatim from legacy ProductCTAWrapper. Passed to the block as the
  // EDD-container mount signal (kept independent of `variant` so the container's EDD IS_VISIBLE
  // telemetry fires in exactly the states it did before).
  const showEDDMsg = productHelper.getIsAgeRestrictedProduct(props.product as Product)
    ? props.type === CTA_TYPE.ADD_TO_CART
    : !(
        props.type === CTA_TYPE.WAITLIST ||
        Boolean(props.selectedVariantPurchaseInfo?.isPreorder) ||
        Boolean(props.selectedVariantPurchaseInfo?.showMadeToOrder) ||
        getIsGiftCard(props.product?.id) ||
        Boolean(props.product?.isMadeToOrder)
      )

  return (
    <ProductButtonBagDS
      variant={variant}
      product={props.product}
      eddDetail={props.selectedVariantPurchaseInfo?.eddDetail}
      selectedInternalVariantId={props.selectedVariantPurchaseInfo?.selectedInternalVariantId}
      isQuickATCForMobile={props.isQuickATCForMobile}
      showEDDMsg={showEDDMsg && !!props.product && !isGoldBarError && !props.isPostOrderFlow}
      showIcon={isMobile}
      inStockMsgClass={props.inStockMsgClass}
      salePrice={props.salePrice}
      festivalOfferPrice={festivalOfferPrice}
      hidePayLater={hidePayLater}
      hideKlarnaFull={hideKlarnaFull}
      klarnaWrapperClassname={klarnaWrapperClassname}
    >
      {/* CTA region keeps the historical price split: it receives `festivalOfferPrice ?? salePrice`. */}
      <ProductCTADS {...ctaProps} salePrice={festivalOfferPrice ?? props.salePrice} />
      <div ref={props.atcSectionEndRef} data-testid={TEST_LOCATORS.PRODUCT_CTA_SECTION_END} />
    </ProductButtonBagDS>
  )
}

export { ProductCTADS }
export default ProductCTADSWrapper
